import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
    IoCheckmarkCircle,
    IoCloseCircle,
    IoDownload,
    IoEye,
} from "react-icons/io5";
import { useBuyPlans } from "../useHook";
import GlobalPagination from "../../../Components/Pagination/GlobalPagination";
import { usePermission, SystemPermissions } from "../../../Utils/permissionAccess";

const LIMIT = 10;

function statusBadgeClass(status) {
    if (status === "completed" || status === "approved") {
        return "bg-green-100 text-green-700";
    }
    if (status === "failed" || status === "rejected") {
        return "bg-red-100 text-red-700";
    }
    return "bg-yellow-100 text-yellow-800";
}

function statusLabel(row) {
    if (row.payment_status === "completed" && row.payment_approved) {
        return "Approved";
    }
    if (row.payment_status === "failed") return "Rejected";
    if (row.payment_status === "pending" && row.payment_screenshot) {
        return "Pending verification";
    }
    if (row.payment_status === "pending") return "Awaiting screenshot";
    return row.payment_status || "—";
}

function ReceiptThumb({ src, alt, onClick }) {
    const [failed, setFailed] = useState(false);
    if (!src || failed) {
        return (
            <div className="w-16 h-16 rounded border border-dashed border-gray-300 bg-gray-50 text-[11px] text-gray-400 flex items-center justify-center text-center px-1">
                No receipt
            </div>
        );
    }
    return (
        <button
            type="button"
            onClick={onClick}
            className="block w-16 h-16 rounded border border-gray-200 overflow-hidden bg-white hover:ring-2 hover:ring-green-500"
            title="View receipt"
        >
            <img
                src={src}
                alt={alt}
                className="w-full h-full object-cover"
                onError={() => setFailed(true)}
            />
        </button>
    );
}

export default function PurchasedHouseDesigns() {
    const navigate = useNavigate();
    const { hasPermission } = usePermission();
    const canReadAll = hasPermission(SystemPermissions.PurchasedHouseDesigns.READ_ALL_PURCHASES);
    const canReadPending = hasPermission(SystemPermissions.PurchasedHouseDesigns.READ_PENDING_VERIFICATION);
    const canReadApproved = hasPermission(SystemPermissions.PurchasedHouseDesigns.READ_APPROVED);
    const canReadRejected = hasPermission(SystemPermissions.PurchasedHouseDesigns.READ_REJECTED);
    const canViewDetail = hasPermission(SystemPermissions.PurchasedHouseDesigns.VIEW_DETAIL);

    const {
        getPurchasedHouseDesigns,
        verifyPurchasedHouseDesignPayment,
    } = useBuyPlans();

    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [offset, setOffset] = useState(0);
    const [activeTab, setActiveTab] = useState("all");
    const [loading, setLoading] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);

    // Automatically set default tab to first allowed tab if "all" is not permitted
    useEffect(() => {
        if (activeTab === "all" && !canReadAll) {
            if (canReadPending) setActiveTab("pending");
            else if (canReadApproved) setActiveTab("approved");
            else if (canReadRejected) setActiveTab("rejected");
        }
    }, [canReadAll, canReadPending, canReadApproved, canReadRejected, activeTab]);

    const loadPurchases = async () => {
        // Guard tab loading
        if (activeTab === "all" && !canReadAll) return;
        if (activeTab === "pending" && !canReadPending) return;
        if (activeTab === "approved" && !canReadApproved) return;
        if (activeTab === "rejected" && !canReadRejected) return;

        setLoading(true);
        try {
            const payload = await getPurchasedHouseDesigns(
                {
                    offset,
                    limit: LIMIT,
                    status: activeTab,
                },
                setRows,
            );
            setTotal(payload.pagination?.totalRecords ?? 0);
        } catch (error) {
            console.error(error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Failed to load purchased house designs.",
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadPurchases();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [offset, activeTab]);

    const handleTab = (tab) => {
        setActiveTab(tab);
        setOffset(0);
    };

    const handleDownload = (imageUrl) => {
        if (!imageUrl) return;
        const link = document.createElement("a");
        link.href = imageUrl;
        link.download = `house-design-payment-${Date.now()}.jpg`;
        link.target = "_blank";
        link.rel = "noreferrer";
        link.click();
    };

    const handleAccept = async (purchaseId) => {
        const result = await Swal.fire({
            title: "Approve payment?",
            text: `Approve payment for purchase #${purchaseId}? The customer will be notified by email.`,
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#22c55e",
            cancelButtonColor: "#6b7280",
            confirmButtonText: "Yes, approve",
            cancelButtonText: "Cancel",
        });

        if (!result.isConfirmed) return;

        try {
            const response = await verifyPurchasedHouseDesignPayment(
                purchaseId,
                "approved",
            );
            if (response.status === 200 && response.data?.success !== false) {
                await loadPurchases();
                Swal.fire({
                    icon: "success",
                    title: "Payment approved",
                    text: response.data?.message || "Status updated successfully.",
                });
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Failed",
                    text: response.data?.message || "Could not update status.",
                });
            }
        } catch (error) {
            console.error(error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text:
                    error.response?.data?.message ||
                    "An error occurred while updating the payment status.",
            });
        }
    };

    const handleReject = async (purchaseId) => {
        const result = await Swal.fire({
            title: "Reject payment?",
            text: `Reject payment for purchase #${purchaseId}? The customer will be notified by email.`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#dc2626",
            cancelButtonColor: "#6b7280",
            confirmButtonText: "Yes, reject",
            cancelButtonText: "Cancel",
        });

        if (!result.isConfirmed) return;

        try {
            const response = await verifyPurchasedHouseDesignPayment(
                purchaseId,
                "rejected",
            );
            if (response.status === 200 && response.data?.success !== false) {
                await loadPurchases();
                Swal.fire({
                    icon: "success",
                    title: "Payment rejected",
                    text: response.data?.message || "Status updated successfully.",
                });
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Failed",
                    text: response.data?.message || "Could not update status.",
                });
            }
        } catch (error) {
            console.error(error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text:
                    error.response?.data?.message ||
                    "An error occurred while updating the payment status.",
            });
        }
    };

    const canVerifyRow = (row) =>
        row.payment_status === "pending" && Boolean(row.payment_screenshot);

    const renderLastColumnHeader = () => "Status";

    const renderVerifyActions = (row) => {
        if (!canVerifyRow(row) || !canReadPending) return null;
        return (
            <div className="flex items-center gap-2">
                <button
                    type="button"
                    onClick={() => handleAccept(row.id)}
                    className="text-green-500 hover:text-green-600 transition-colors"
                    title="Approve payment"
                >
                    <IoCheckmarkCircle size={24} />
                </button>
                <button
                    type="button"
                    onClick={() => handleReject(row.id)}
                    className="text-red-500 hover:text-red-600 transition-colors"
                    title="Reject payment"
                >
                    <IoCloseCircle size={24} />
                </button>
            </div>
        );
    };

    const renderLastColumnCell = (row) => {
        return (
            <td className="table-td">
                <div className="flex items-center gap-2 whitespace-nowrap">
                    <span
                        className={`capitalize px-2 py-1 rounded text-xs font-semibold ${statusBadgeClass(
                            row.payment_status,
                        )}`}
                    >
                        {statusLabel(row)}
                    </span>
                    {renderVerifyActions(row)}
                </div>
            </td>
        );
    };

    const renderTable = (data) => (
        <div className="overflow-x-auto mt-6">
            <table className="min-w-full table-auto bg-white shadow-md rounded-lg overflow-hidden">
                <thead>
                    <tr className="brand-gradient text-white">
                        <th className="table-th">#</th>
                        <th className="table-th">Customer</th>
                        <th className="table-th">House design</th>
                        <th className="table-th">Amount</th>
                        <th className="table-th">Garage</th>
                        <th className="table-th">Screenshot</th>
                        <th className="table-th">{renderLastColumnHeader()}</th>
                        {canViewDetail && <th className="table-th">Details</th>}
                    </tr>
                </thead>
                <tbody>
                    {loading ? (
                        <tr>
                            <td colSpan={canViewDetail ? 8 : 7} className="table-td text-center text-gray-500">
                                Loading…
                            </td>
                        </tr>
                    ) : data.length === 0 ? (
                        <tr>
                            <td colSpan={canViewDetail ? 8 : 7} className="table-td text-center text-gray-500 italic">
                                No records found
                            </td>
                        </tr>
                    ) : (
                        data.map((row, index) => {
                            const user = row.user || {};
                            const hd = row.house_design || {};
                            const shotUrl = row.payment_screenshot_url || null;
                            return (
                                <tr
                                    key={row.id}
                                    className={`${
                                        index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"
                                    } hover:bg-gray-200 transition-colors duration-300`}
                                >
                                    <td className="table-td">{offset + index + 1}</td>
                                    <td className="table-td">
                                        <div className="font-medium text-gray-900">
                                            {user.firstName || ""} {user.lastName || ""}
                                        </div>
                                        <div className="text-gray-500 text-xs truncate">{user.email}</div>
                                        {user.contactNo && (
                                            <div className="text-gray-400 text-xs">{user.contactNo}</div>
                                        )}
                                    </td>
                                    <td className="table-td">
                                        <span className="line-clamp-2">{hd.title || "—"}</span>
                                    </td>
                                    <td className="table-td">
                                        Rs.{" "}
                                        {row.price != null
                                            ? Number(row.price).toLocaleString()
                                            : "—"}
                                    </td>
                                    <td className="table-td capitalize">
                                        {row.garage_location || "—"}
                                    </td>
                                    <td className="table-td">
                                        <ReceiptThumb
                                            src={shotUrl}
                                            alt={`Receipt for purchase #${row.id}`}
                                            onClick={() => shotUrl && setPreviewUrl(shotUrl)}
                                        />
                                    </td>
                                    {renderLastColumnCell(row)}
                                    {canViewDetail && (
                                        <td className="table-td">
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    navigate(
                                                        `/purchased-house-designs/view/${row.id}`,
                                                    )
                                                }
                                                className="btn btn-view text-white"
                                            >
                                                <IoEye className="text-white" /> View
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            );
                        })
                    )}
                </tbody>
            </table>
        </div>
    );

    return (
        <div className="p-6">
            <div className="bg-gray-100 p-4 flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Purchased house designs</h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 mb-6">
                {canReadAll && (
                    <button
                        type="button"
                        onClick={() => handleTab("all")}
                        className={`px-4 py-2 rounded w-full text-center ${activeTab === "all"
                            ? "btn btn-view"
                            : "btn btn-primary"
                            }`}
                    >
                        All purchases
                    </button>
                )}
                {canReadPending && (
                    <button
                        type="button"
                        onClick={() => handleTab("pending")}
                        className={`px-4 py-2 rounded w-full text-center ${activeTab === "pending"
                            ? "btn btn-view"
                            : "btn btn-primary"
                            }`}
                    >
                        Pending verification
                    </button>
                )}
                {canReadApproved && (
                    <button
                        type="button"
                        onClick={() => handleTab("approved")}
                        className={`px-4 py-2 rounded w-full text-center ${activeTab === "approved"
                            ? "btn btn-view"
                            : "btn btn-primary"
                            }`}
                    >
                        Approved
                    </button>
                )}
                {canReadRejected && (
                    <button
                        type="button"
                        onClick={() => handleTab("rejected")}
                        className={`px-4 py-2 rounded w-full text-center ${activeTab === "rejected"
                            ? "btn btn-view"
                            : "btn btn-primary"
                            }`}
                    >
                        Rejected
                    </button>
                )}
            </div>

            {renderTable(rows)}

            <div className="mt-6">
                <GlobalPagination
                    offset={offset}
                    limit={LIMIT}
                    total={total}
                    onPageChange={setOffset}
                />
            </div>

            {previewUrl && (
                <div
                    className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4"
                    onClick={() => setPreviewUrl(null)}
                >
                    <div
                        className="relative max-w-3xl w-full"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            type="button"
                            className="absolute -top-10 right-0 text-white text-2xl font-bold"
                            onClick={() => setPreviewUrl(null)}
                        >
                            ×
                        </button>
                        <img
                            src={previewUrl}
                            alt="Payment screenshot"
                            className="max-w-full max-h-[80vh] mx-auto object-contain rounded shadow-lg"
                        />
                        <div className="mt-4 flex justify-center">
                            <button
                                type="button"
                                onClick={() => handleDownload(previewUrl)}
                                className="btn btn-action text-white justify-center"
                            >
                                <IoDownload /> Download
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
